import { CSSProperties, FC, ReactElement, RefObject, isValidElement } from 'react';

import Button from '@componentLibrary/buttons/Button';
import clsx from 'clsx';

import Paper from '@components/layout/Paper';

import { isEmpty, noop } from '@helpers/util';

import './EmptyStateDisplay.scss';

interface ActionButtonProps {
    // Todo: Name this `actionButtonProps` or something similar?
    label: string;
    onClick: (e: any) => any;
    startIcon?: ReactElement;
    disabled?: boolean;
    tooltip?: string;
}

export interface EmptyStateDisplayProps {
    divRef?: RefObject<HTMLDivElement>;
    graphic: ReactElement;
    className?: string;
    contentClassName?: string;
    customContent?: ReactElement;
    label?: string;
    smallText?: string | ReactElement;
    actionButton?: ActionButtonProps | ReactElement | null;
    style?: CSSProperties;
    layout?: 'vertical' | 'horizontal';
    addPaperWrapper?: boolean;
}

const EmptyStateDisplay: FC<EmptyStateDisplayProps> = ({
    divRef,
    graphic,
    className = '',
    contentClassName = '',
    customContent,
    label,
    smallText,
    actionButton = null,
    style,
    layout = 'vertical',
    addPaperWrapper = false,
}) => {
    // Render action button inline instead of as a separate component
    // to avoid React reconciliation issues with event handlers
    const renderActionButton = () => {
        if (!actionButton) {
            return null;
        }

        return isValidElement(actionButton) ? (
            actionButton
        ) : (
            <Button
                className="empty-state_fallback-action-button"
                variant="filled"
                color="blue"
                onClick={(actionButton as ActionButtonProps)?.onClick || noop}
                startIcon={(actionButton as ActionButtonProps)?.startIcon}
                disabled={(actionButton as ActionButtonProps)?.disabled}
                tooltip={(actionButton as ActionButtonProps)?.tooltip}
            >
                {(actionButton as ActionButtonProps)?.label}
            </Button>
        );
    };

    // Render empty state content inline instead of as a separate component
    // to avoid React reconciliation issues with event handlers
    const emptyStateContent = (
        <div ref={divRef} className={clsx('empty-state-display', className, layout)} style={style}>
            <div className="empty-state-display_graphic">{graphic}</div>
            <div className={clsx('content', contentClassName)}>
                {!isEmpty(customContent) && customContent}
                {!isEmpty(label) &&
                    (isValidElement(label) ? (
                        <div className="empty-state-display_label">{label}</div>
                    ) : (
                        <p className="empty-state-display_label">{label}</p>
                    ))}
                {!isEmpty(smallText) &&
                    (isValidElement(smallText) ? (
                        <div className="empty-state-display_small-text">{smallText}</div>
                    ) : (
                        <p className="empty-state-display_small-text">{smallText}</p>
                    ))}
                {!isEmpty(actionButton) && renderActionButton()}
            </div>
        </div>
    );

    return (
        <>
            {addPaperWrapper ? (
                <Paper sx={{ minHeight: '462px', display: 'flex', alignItems: 'center' }}>
                    {emptyStateContent}
                </Paper>
            ) : (
                emptyStateContent
            )}
        </>
    );
};

export default EmptyStateDisplay;
